<template>
  <teleport to="body">
    <transition name="lightbox-anim">
      <div v-if="foto" class="overlay-lightbox" @click.self="$emit('fechar')" role="dialog" aria-modal="true">
        <div class="lightbox-conteudo" @click.stop>
          <button type="button" class="btn-fechar-lightbox" @click="$emit('fechar')" title="Fechar visualização (Esc)"
            aria-label="Fechar visualização">
            <i class="ti ti-x"></i>
          </button>

          <div class="lightbox-imagem-box">
            <slot name="imagem" :foto="foto">
              <img :src="getUrl(foto)" :alt="getAlt(foto)" />
            </slot>
          </div>

          <div class="lightbox-legenda">
            <div class="lightbox-legenda-cabecalho">
              <slot name="descricao" :foto="foto">
                <p class="lightbox-texto-descricao">
                  {{ getDescricao(foto) || "Sem descrição" }}
                </p>
              </slot>

              <!-- Slot ou controle de visibilidade (Pública / Privada) -->
              <slot name="visibilidade" :foto="foto">
                <div v-if="podeAlterarVisibilidade" class="lightbox-visibilidade-controle">
                  <span class="lightbox-visibilidade-rotulo">Visibilidade:</span>
                  <div class="seletor-visibilidade-lightbox">
                    <button type="button" class="btn-visibilidade-pill is-privada" :class="{ 'ativo': !foto.isPublica }"
                      :disabled="alterandoVisibilidade" @click="$emit('alterar-visibilidade', false)"
                      title="Tornar imagem privada">
                      <i class="ti ti-lock"></i> Privada
                    </button>
                    <button type="button" class="btn-visibilidade-pill is-publica" :class="{ 'ativo': foto.isPublica }"
                      :disabled="alterandoVisibilidade" @click="$emit('alterar-visibilidade', true)"
                      title="Tornar imagem pública">
                      <i class="ti ti-world"></i> Pública
                    </button>
                  </div>
                  <span v-if="alterandoVisibilidade" class="visibilidade-loading">
                    <i class="ti ti-loader spin"></i>
                  </span>
                </div>
              </slot>
            </div>

            <div class="lightbox-meta">
              <slot name="meta" :foto="foto">
                <span v-if="getData(foto)">
                  <i class="ti ti-calendar-time"></i> Registrada em
                  {{ getData(foto) }}
                </span>
                <span v-else></span>
              </slot>

              <slot name="acoes" :foto="foto">
                <button v-if="podeRemover" type="button" class="btn-remover-lightbox" @click="$emit('remover', foto)">
                  <i class="ti ti-trash"></i> Remover Foto
                </button>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { onMounted, onUnmounted } from "vue";

const props = defineProps({
  foto: {
    type: Object,
    default: null,
  },
  resolverUrl: {
    type: Function,
    default: null,
  },
  obterDescricao: {
    type: Function,
    default: null,
  },
  obterAlt: {
    type: Function,
    default: null,
  },
  obterData: {
    type: Function,
    default: null,
  },
  podeAlterarVisibilidade: {
    type: Boolean,
    default: false,
  },
  alterandoVisibilidade: {
    type: Boolean,
    default: false,
  },
  podeRemover: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["fechar", "remover", "alterar-visibilidade"]);

function getUrl(item) {
  if (props.resolverUrl) return props.resolverUrl(item);
  if (!item) return "";
  if (item.imagemUrl) return item.imagemUrl;
  if (item.url) return item.url;
  if (item.imagem?.url) return item.imagem.url;
  if (item.imagem?.base64Data) {
    return `data:${item.imagem.mimeType || "image/jpeg"};base64,${item.imagem.base64Data}`;
  }
  if (item.dados) return `data:image/jpeg;base64,${item.dados}`;
  if (item.caminho) return item.caminho;
  return "";
}

function getDescricao(item) {
  if (props.obterDescricao) return props.obterDescricao(item);
  return item?.descricao || "";
}

function getAlt(item) {
  if (props.obterAlt) return props.obterAlt(item);
  return item?.descricao || "Foto ampliada";
}

function getData(item) {
  if (props.obterData) return props.obterData(item);
  return "";
}

function onKeyDown(e) {
  if (e.key === "Escape" && props.foto) {
    emit("fechar");
  }
}

onMounted(() => {
  window.addEventListener("keydown", onKeyDown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeyDown);
});
</script>

<style scoped>
.overlay-lightbox {
  position: fixed;
  inset: 0;
  background-color: rgba(5, 8, 18, 0.92);
  backdrop-filter: blur(8px);
  z-index: 901;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  box-sizing: border-box;
}

.lightbox-conteudo {
  position: relative;
  max-width: 900px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background-color: #121827;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
}

.btn-fechar-lightbox {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  border: none;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background 0.2s;
}

.btn-fechar-lightbox:hover {
  background-color: rgba(220, 38, 38, 0.9);
}

.lightbox-imagem-box {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0b0f19;
  max-height: 68vh;
  overflow: hidden;
}

.lightbox-imagem-box img {
  max-width: 100%;
  max-height: 68vh;
  object-fit: contain;
  display: block;
}

.lightbox-legenda {
  padding: 16px 20px;
  background-color: #182033;
  color: #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lightbox-legenda-cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.lightbox-texto-descricao {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: #e2e8f0;
  line-height: 1.4;
  flex: 1;
}

.lightbox-visibilidade-controle {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: rgba(11, 15, 25, 0.6);
  padding: 4px 10px;
  border-radius: 99px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.lightbox-visibilidade-rotulo {
  font-size: 0.78rem;
  color: #94a3b8;
  font-weight: 500;
}

.seletor-visibilidade-lightbox {
  display: flex;
  gap: 4px;
  background-color: rgba(0, 0, 0, 0.4);
  padding: 2px;
  border-radius: 99px;
}

.btn-visibilidade-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  color: #94a3b8;
  border: none;
  padding: 4px 10px;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-visibilidade-pill:hover:not(:disabled) {
  color: #ffffff;
}

.btn-visibilidade-pill.ativo {
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.btn-visibilidade-pill.ativo.is-privada {
  background-color: #475569;
}

.btn-visibilidade-pill.ativo.is-publica {
  background-color: #10b981;
}

.btn-visibilidade-pill:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.visibilidade-loading {
  color: #94a3b8;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
}

.lightbox-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.78rem;
  color: #94a3b8;
}

.btn-remover-lightbox {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: rgba(220, 38, 38, 0.2);
  color: #f87171;
  border: 1px solid rgba(220, 38, 38, 0.4);
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-remover-lightbox:hover {
  background-color: rgb(220, 38, 38);
  color: #ffffff;
}

@keyframes girar {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.spin {
  animation: girar 0.9s linear infinite;
}

.lightbox-anim-enter-active,
.lightbox-anim-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-anim-enter-from,
.lightbox-anim-leave-to {
  opacity: 0;
}
</style>
